import React, { useState } from 'react'

import { XIcon } from 'lucide-react'

import { Box, IconButton, Stack, Typography, alpha, useTheme } from '@mui/material'

import { FieldLabel, Skeleton, fieldBorderColor, fieldFill } from '@repo/ui'

import { useSnackbar } from 'contexts'
import {
  SCAN_MEDIA_ACCEPT,
  SCAN_MEDIA_MAX_SIZE_MB,
  uploadScanMedia,
} from 'services/mediaApi'

// Direct path, NOT the `components` barrel: this file is itself re-exported by that barrel,
// so going through it would close a cycle (components → input → ScanMediaField → components)
// and resolve to undefined at module-init time — an empty render with no error pointing here.
// Same reasoning FileUploadField records for its own import of ImageGalleryModal.
import { FileUploadField } from './FileUploadField'
// Direct file path, NOT the `components` barrel, for the same cycle reason as the line above.
import { TagLocationIcon } from '../../display/TagLocationIcon/TagLocationIcon'

/**
 * Dropzone / preview height.
 *
 * 140 rather than FileUploadField's 268px default, which is sized for a product-image
 * gallery and would own a settings card. Below 200px the dropzone switches to its compact
 * prompt automatically, which is what makes 140 legible rather than clipped.
 */
const FIELD_HEIGHT = 140

/**
 * Whether a stored URL points at a video.
 *
 * Extension only: the value is a bare string on a text column, so there is no MIME type to
 * read (unlike FileUploadField, which holds real `File`s and can check `type` first). The
 * query string is stripped before matching so a signed/cache-busted URL still resolves —
 * `…/clip.mp4?v=2` is a video.
 */
const isVideoUrl = (url: string): boolean =>
  /\.(mp4|m4v|mov|webm)$/i.test(url.split(/[?#]/)[0])

export interface ScanMediaFieldProps {
  label?: string
  /** The stored URL, or null/undefined when nothing has been uploaded yet. */
  value?: string | null
  /** Called with the uploaded URL, or `null` when the user clears it. */
  onChange: (url: string | null) => void
  disabled?: boolean
  /**
   * Inline content beside the label — in practice an `InfoTooltip`. Mirrors the prop of
   * the same name on `TextField`, because the two are used side by side on Settings →
   * Fields → Defaults and a tooltip on one field but not its neighbour would read as an
   * oversight rather than a decision.
   *
   * Composed with the existing `(optional)` marker rather than replacing it (§67: the
   * tooltip answers WHERE the value applies, which is a different question from whether
   * the field must be filled in).
   */
  labelAdornment?: React.ReactNode
  /**
   * VRG-720 — the contact card's portrait reuses this control (same upload endpoint, same
   * preview, same clear affordance) rather than a second hand-rolled one (§36), so the parts
   * that are specific to "where to scan" are overridable. Every default is the original
   * behaviour, so no existing call site changes.
   *
   * Accepted file types, in react-dropzone's shape. Defaults to images AND video.
   */
  accept?: Record<string, string[]>
  /** The dropzone's second line. Defaults to "Images or video". */
  description?: string
  /** Alt text of the preview image. Defaults to "Where to scan". */
  alt?: string
  /** The dropzone's prompt glyph. Defaults to the tag-location pin. */
  icon?: React.ComponentType<{ size?: number; strokeWidth?: number }>
  /** Title of the full-size gallery. Defaults to "Where to scan". */
  galleryTitle?: string
}

/**
 * One image or video showing WHERE the tag sits on an item.
 *
 * Shared deliberately (§36): the product edit form and the company-wide default in Settings →
 * Installation are the same control over the same contract, and a second hand-roll is how the
 * two would end up accepting different file types or advertising different size caps.
 *
 * The upload is its own request — `POST /media/admin/scan-media` returns a URL, and the
 * product/company save that follows is ordinary JSON carrying that string. So there is no
 * multipart involvement in the record's own save, and `value` is just text.
 */
export const ScanMediaField = ({
  label,
  value,
  onChange,
  disabled = false,
  labelAdornment,
  accept = SCAN_MEDIA_ACCEPT,
  description = 'Images or video',
  alt = 'Where to scan',
  icon = TagLocationIcon,
  galleryTitle = 'Where to scan',
}: ScanMediaFieldProps) => {
  const theme = useTheme()
  /*
    ONE cast, for the field-chrome helpers below — the same workaround FileUploadField
    documents: the pnpm store holds two @mui/material copies, so the `Theme` this file's
    `useTheme()` returns is nominally incompatible with the `Theme` in @repo/ui's helper
    signatures. Same object at runtime, different declaration file. Real fix is VRG-315.
  */
  const fieldTheme = theme as Parameters<typeof fieldFill>[0]

  const { open: notification } = useSnackbar()

  const [isUploading, setIsUploading] = useState(false)
  /*
    Bumped to REMOUNT FileUploadField, which owns its own file list internally. Without it a
    failed upload leaves the rejected file sitting in the dropzone's list, so the user is
    looking at a file the server never accepted and has no way to retry the same one.
  */
  const [dropzoneKey, setDropzoneKey] = useState(0)

  const handleFiles = async (files: File[]) => {
    // FileUploadField emits `[]` on mount, and again after the remount below. Neither is a
    // user clearing anything — the clear affordance is the X on the preview — so an empty
    // list is ignored rather than treated as `onChange(null)`.
    const file = files[0]
    if (!file || isUploading) return

    setIsUploading(true)
    try {
      const { path } = await uploadScanMedia(file)
      onChange(path)
    } catch (error: any) {
      notification({
        type: 'error',
        title: error?.message || "Couldn't upload that file",
      })
      // Previous value stays intact — a failed upload replaces nothing.
      setDropzoneKey((k) => k + 1)
    } finally {
      setIsUploading(false)
    }
  }

  const handleClear = () => {
    onChange(null)
    // The dropzone that reappears must be empty; it still holds whatever was dropped last.
    setDropzoneKey((k) => k + 1)
  }

  return (
    // ONE element in the layout (§31): label, control and helper live in a single container,
    // so a parent `<Stack spacing={n}>` separates FIELDS and never gets between this label
    // and its control — which is what silently overrode FieldLabel's 8px gap elsewhere.
    <Box sx={{ width: '100%' }}>
      {/*
        The bare string stays the no-adornment path on purpose: `FieldLabel` styles a
        STRING adornment itself (12px, muted) and cannot style one buried in a Stack, so
        composing unconditionally would silently restyle every existing call site. When a
        tooltip is present the marker is wrapped to carry that same treatment by hand.
      */}
      <FieldLabel
        label={label}
        adornment={
          labelAdornment
            ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                {/*
                  A `Box`, not a `Typography`, and `lineHeight: inherit` spelled out.
                  `FieldLabel` pins its adornment slot to the LABEL's line box so an
                  adornment can never grow the row and push the control down — measured
                  here first as a 24px label row against the Instructions field's 19.9,
                  because a `Typography` brings `body1`'s own line-height and won that
                  cascade. The 12px/muted pair is what `FieldLabel` applies to a bare
                  string adornment; this reproduces it without the variant.
                */}
                <Box
                  component="span"
                  sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 'inherit' }}
                >
                  (optional)
                </Box>
                {labelAdornment}
              </Stack>
            )
            : '(optional)'
        }
      />

      {value ? (
        <Box
          sx={{
            position: 'relative',
            height: FIELD_HEIGHT,
            width: '100%',
            overflow: 'hidden',
            borderRadius: 2,
            // Same rest border every other field carries (§31), from the shared helper
            // rather than a local token that would drift.
            border: `1px solid ${fieldBorderColor(fieldTheme)}`,
            backgroundColor: fieldFill(fieldTheme),
          }}
        >
          {isVideoUrl(value) ? (
            <Box
              component="video"
              src={value}
              controls
              preload="metadata"
              playsInline
              muted
              sx={{ height: '100%', width: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <Box
              component="img"
              src={value}
              alt={alt}
              sx={{ height: '100%', width: '100%', objectFit: 'contain', display: 'block' }}
            />
          )}

          {/* X, not a trash can (§38): this severs the association between the record and a
              stored file. Nothing is destroyed and it is cheap to undo by uploading again,
              so it takes no confirmation either. */}
          {!disabled && (
            <IconButton
              onClick={handleClear}
              aria-label="Remove this media"
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: alpha(theme.palette.common.black, 0.45),
                color: theme.palette.common.white,
                '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.65) },
              }}
            >
              <XIcon size={18} strokeWidth={1} />
            </IconButton>
          )}
        </Box>
      ) : isUploading ? (
        // §19 — footprint parity, not a spinner in an empty box: the placeholder occupies the
        // exact space the dropzone (and then the preview) occupies, so nothing shifts when the
        // upload lands.
        <Stack spacing={2}>
          <Skeleton variant="rounded" width="100%" height={FIELD_HEIGHT} />
          <Typography variant="body4" color="text.secondary">
            Uploading…
          </Typography>
        </Stack>
      ) : (
        <Box
          sx={{
            ...(disabled && { opacity: 0.5, pointerEvents: 'none' }),
          }}
          aria-disabled={disabled || undefined}
        >
          <FileUploadField
            key={dropzoneKey}
            acceptFiles={accept}
            /*
              A product outline carrying a location pin — the still frame of the thing this field
              holds: "here is where the tag sits on this item".

              It was lucide's `SmartphoneNfc`, chosen to match the glyph the customer app falls back
              to in WhereToScanPanel. That matched the wrong thing. What the brand uploads here is a
              picture of THEIR OBJECT with the tag's position marked on it; a phone is what the
              media explains, not what it contains, so the prompt was describing the reader rather
              than the subject.

              Not the generic upload cloud either, for the reason the phone was picked over it: the
              empty state should say what to put here, not merely that something can be dropped.
            */
            icon={icon}
            description={description}
            maxFiles={1}
            maxSizeMb={SCAN_MEDIA_MAX_SIZE_MB}
            showMaxFileSize
            height={FIELD_HEIGHT}
            galleryTitle={galleryTitle}
            onFilesChange={(files) => {
              // FileUploadField hands back its own wrapper ({id, file, …}), not native
              // Files — same unwrap as the support form and useProductMutations.
              void handleFiles(
                (files as unknown as { file: File }[]).map((wrapper) => wrapper.file),
              )
            }}
          />
        </Box>
      )}
    </Box>
  )
}
